// image-config-doc.ts
import { Quat, Vec3 } from 'playcanvas';
import { Events } from './events';
import { Scene } from './scene';
import { localize } from './ui/localization';

// --- Types from your COLMAP-exported JSON ---
type ColmapPose = {
    image_id?: number;
    qw: number;
    qx: number;
    qy: number;
    qz: number;
    tx: number;
    ty: number;
    tz: number;
    camera_id?: number;
    name: string; // filename (e.g., "0001.png")
};

// Engine-side pose record (camera→world)
type EnginePose = {
    name: string;
    position: Vec3; // camera center in world space
    rotation: Quat; // camera→world rotation
};

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

// Accept spec for showOpenFilePicker
const PoseJsonFileTypes: FilePickerAcceptType[] = [{
    description: 'Pose JSON (COLMAP)',
    accept: {
        'application/json': ['.json']
    }
}];

// Fallback single-file selector (when File System Access API is not available)
type SingleFileSelectorCallback = (file: File | null) => void;

class SingleFileSelector {
    show: (cb: SingleFileSelectorCallback) => void;

    constructor(accept: string) {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', accept);
        input.setAttribute('multiple', 'false');
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);

        let handler: SingleFileSelectorCallback | null = null;

        input.addEventListener('change', () => {
            const f = input.files && input.files[0] ? input.files[0] : null;
            input.value = ''; // allow re-selecting same file again
            handler?.(f);
        });

        input.addEventListener('cancel', () => {
            handler?.(null);
        });

        this.show = (cb: SingleFileSelectorCallback) => {
            handler = cb;
            input.click();
        };
    }
}

// ---------- COLMAP -> Engine conversion ----------
function colmapToEnginePose(p: ColmapPose): EnginePose {
    const q_wc = new Quat(p.qx, p.qy, p.qz, p.qw).normalize();
    const q_cw = q_wc.clone().invert().normalize();
    const t_wc = new Vec3(p.tx, p.ty, p.tz);
    const C = q_cw.transformVector(t_wc.clone().mulScalar(-1));

    return {
        name: p.name,
        position: C,
        rotation: q_cw
    };
}

async function readPoseJson(file: File): Promise<EnginePose[]> {
    const text = await file.text();
    let data: unknown;
    try {
        data = JSON.parse(text);
    } catch (e) {
        throw new Error('Invalid JSON.');
    }

    if (!Array.isArray(data)) {
        throw new Error('Pose JSON must be an array.');
    }

    const poses: EnginePose[] = [];
    for (const entry of data as ColmapPose[]) {
        if (
            entry &&
            typeof entry.name === 'string' &&
            [entry.qw, entry.qx, entry.qy, entry.qz, entry.tx, entry.ty, entry.tz].every((n) => typeof n === 'number')
        ) {
            poses.push(colmapToEnginePose(entry));
        }
    }

    if (poses.length === 0) {
        throw new Error('No valid poses found in JSON.');
    }

    return poses;
}

// ---------- Register events (MODIFIED) ----------
const registerPoseJsonEvents = (_scene: Scene, events: Events) => {
    // In-memory store
    let loadedFileName: string | null = null;
    const originalPoses: EnginePose[] = [];     // Stores the clean, original data
    const transformedPoses: EnginePose[] = [];  // This is now the 'live' data

    // Helper: replace all poses
    const setPoses = (list: EnginePose[], fileName?: string) => {
        originalPoses.length = 0;
        transformedPoses.length = 0;

        // Create deep copies for both original and live data
        list.forEach(p => {
            originalPoses.push({ name: p.name, position: p.position.clone(), rotation: p.rotation.clone() });
            transformedPoses.push({ name: p.name, position: p.position.clone(), rotation: p.rotation.clone() });
        });

        loadedFileName = fileName ?? null;
        events.fire('poses.loaded', { count: transformedPoses.length, fileName: loadedFileName, poses: transformedPoses });
    };

    // Helper: set the live transformed poses
    const setTransformed = (list: EnginePose[]) => {
        transformedPoses.length = 0;
        transformedPoses.push(...list);
        events.fire('poses.transformed'); // Notify system that live poses have changed (optional, currently unused listener)
    };

    // Helper: show popup
    const showError = async (msg: string) => {
        await events.invoke('showPopup', {
            type: 'error',
            header: localize('pose.load-failed') ?? 'Load Failed',
            message: msg
        });
    };

    // Open via picker (with fallback)
    events.function('poses.open', async () => {
        const hasPicker = !!(window as any).showOpenFilePicker;
        const selector = hasPicker ? null : new SingleFileSelector('.json');
        events.fire('startSpinner');

        try {
            if (selector) {
                const file = await new Promise<File | null>((resolve) => {
                    selector.show((f) => resolve(f));
                });
                if (!file) return false;

                const list = await readPoseJson(file);
                setPoses(list, file.name);
                return true;
            } else {
                const handles: FileSystemFileHandle[] = await (window as any).showOpenFilePicker({
                    id: 'PoseJsonOpen',
                    multiple: false,
                    types: PoseJsonFileTypes
                });
                if (!handles?.length) return false;

                const file = await handles[0].getFile();
                const list = await readPoseJson(file);
                setPoses(list, file.name);
                return true;
            }
        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                console.error(err);
                await showError(`'${err?.message ?? err}'`);
            }
            return false;
        } finally {
            events.fire('stopSpinner');
        }
    });

    // Programmatic load: pass a File
    events.function('poses.load', async (file: File) => {
        if (!(file instanceof File)) {
            await showError('poses.load requires a File.');
            return false;
        }
        events.fire('startSpinner');
        try {
            const list = await readPoseJson(file);
            setPoses(list, file.name);
            return true;
        } catch (err: any) {
            console.error(err);
            await showError(`'${err?.message ?? err}'`);
            return false;
        } finally {
            events.fire('stopSpinner');
        }
    });

    // Accessors (MODIFIED)
    events.function('poses.list', (): EnginePose[] => transformedPoses.slice()); // Returns live, transformed poses
    events.function('poses.originalList', (): EnginePose[] => originalPoses.slice()); // Returns original, unmodified poses
    events.function('poses.setTransformed', setTransformed); // Allows external update of live poses
    events.function('poses.count', (): number => transformedPoses.length);
    events.function('poses.fileName', (): string | null => loadedFileName);

    // Clear (MODIFIED)
    events.function('poses.clear', () => {
        originalPoses.length = 0;
        transformedPoses.length = 0;
        loadedFileName = null;
        events.fire('poses.cleared');
        return true;
    });
};

export { registerPoseJsonEvents };
export type { EnginePose, ColmapPose };
